import * as React from "react";

import * as PropTypes from "prop-types";
import RequestBrandAccess from "./request-brand-access.js";
import BuyerProfileModal from "../buyer-profile-modal";
import { format } from "lib/globalize-util.js";
import Dialog from "components/dialog";
import Button from "components/form/button";
import get from "lodash/get";
import EmailIcon from "components/icons/email";
import CartIcon from "components/icons/cart";
import ReviewIcon from "components/icons/find-in-page";
import { $, media, merge, presets } from "glamor";
import theme from "styles/theme";
import { Set } from "immutable";
import { DATATEST } from "constants/integration";

export const Reasons = {
    PRODUCT_INFO: "Need to see more product information and pricing",
    ORDER: "Ready to place an order",
    FOLLOW_UP: "Following up on a trade show contact or prior email",
    BRAND_INFO: "Just curious and want to get more info about this brand",
};

interface IBrandInfo {
    _id: string;
    name?: string;
    dashboard_logo_image?: string;
    initials?: string;
}

interface IProps {
    brandInfo: IBrandInfo;
    onCloseDialog: () => void;
    onMakeAccessRequest: (id: string, data: any) => Promise<{ error: any }>;
    disabled?: boolean;
    onCreateBuyerProfile: (brandInfo: IBrandInfo) => void;
    isBrandPageOfShowSite: boolean;
    onClearCompany: () => void;
    onClearBrand: (brandId: string) => void;
}

interface IState {
    retailerId: string | null;
    message: string;
    reasons: Set<any>;
    allowErrors: boolean;
    confirmOpen: boolean;
}

/**
 * Displays request access or retailer dialog due to access permissions.
 */
export default class RequestAccessPopup extends React.PureComponent<IProps, IState> {
    static contextTypes = {
        me: PropTypes.object.isRequired,
        router: PropTypes.object.isRequired,
    };

    styles = merge(
        $(" .dialogButton", {
            margin: "10px !important",
        }),
        $(" .popupTitle", {
            textAlign: "center",
            fontSize: "25px !important",
        }),
        $(" .header", {
            textAlign: "center",
            fontSize: "20px",
            color: theme.palette.darkestGrey,
        }),
        $(" .sectionIcon", {
            float: "left",
            color: `${theme.palette.primary1Color} !important`,
            height: "55px !important",
            width: "55px !important",
        }),
        $(" .section", {
            float: "left",
            width: "100%",
            marginTop: "21px",
        }),
        $(" .sectionText", {
            margin: "16px 0 0 75px",
            color: theme.palette.darkestGrey,
            fontSize: "18px",
        }),
        $(" .addition", {
            marginTop: "280px",
            fontStyle: "italic",
        }),
        media(
            presets.mobile,
            $(" .sectionIcon", {
                float: "left",
                color: `${theme.palette.primary1Color} !important`,
                height: "45px !important",
                width: "45px !important",
            }),
            $(" .sectionText", {
                margin: "16px 0 0 75px",
                color: theme.palette.darkestGrey,
                fontSize: "12px",
            })
        )
    );

    UNSAFE_componentWillMount() {
        const { me } = this.context;
        this.setState({ retailerId: get(me, "retailers.0._id", null), reasons: Set() });
    }

    render() {
        const { me } = this.context;
        const { brandInfo, disabled, onCloseDialog } = this.props;
        const { message, reasons, retailerId, confirmOpen, allowErrors } = this.state;
        if (confirmOpen) {
            return this.renderConfirm();
        } else if (me.canConnectToBrand()) {
            // buyer has valid retailer profile in our system
            return (
                <RequestBrandAccess
                    allowErrors={allowErrors}
                    brandInfo={brandInfo}
                    onCloseDialog={onCloseDialog}
                    onChange={this.handleChange}
                    reasons={reasons}
                    message={message}
                    onSubmitRequest={this.handleSubmit}
                    disabled={disabled}
                    retailerId={retailerId}
                    onRetailerChange={this.handleRetailerChange}
                />
            );
        } else if (me.isGuestBuyer() && !me.buyer_form_submitted) {
            // buyer does not have a retailer profile.
            return <BuyerProfileModal onSubmit={this.handleCreateBuyerProfile} onRequestClose={onCloseDialog} />;
        } else if (me.isGuestBuyer() && me.buyer_form_submitted) {
            // buyer is guest, has filled out form but has not been approved from salesforce side of thigns
            return (
                <Dialog
                    title={format("Retailer Profile is in Review")}
                    open={true}
                    onRequestClose={onCloseDialog}
                    actions={[
                        <Button key="dismiss" primary={true} label={format("Dismiss")} onClick={onCloseDialog} />,
                    ]}
                >
                    <p>
                        {format(
                            "Before we unlock the ability to request access to our NuORDER brands, your retailer profile must be approved."
                        )}
                    </p>
                </Dialog>
            );
        } else {
            return null;
        }
    }

    private handleCreateBuyerProfile = () => {
        this.props.onCreateBuyerProfile(this.props.brandInfo);
    };

    private handleRetailerChange = (e, key: string, retailerId: string) => this.setState({ retailerId });

    private handleChange = (event, data: string | boolean) => {
        if (typeof data === "boolean") {
            const value = String(event.target.value);
            const { reasons } = this.state;
            const newSet = data ? reasons.add(value) : reasons.remove(value);
            this.setState({ reasons: newSet, allowErrors: true });
        } else {
            this.setState({ message: data });
        }
    };

    private handleSubmit = () => {
        const { brandInfo, onMakeAccessRequest } = this.props;
        const { reasons, retailerId, message } = this.state;
        if (reasons.isEmpty()) {
            this.setState({ allowErrors: true });
            return;
        }
        const data = {
            retailer_id: retailerId,
            message,
            request_reasons: reasons,
        };

        onMakeAccessRequest(brandInfo._id, data).then(({ error }) => {
            if (!error) {
                this.setState({ message: "", reasons: Set(), confirmOpen: true });
            }
        });
    };
    private redirectToSitePage = () => {
        const { router } = this.context;
        const { brandInfo, onClearCompany, onClearBrand } = this.props;
        const siteHomePageUrl = this.getSiteHomepage();
        // Need to remove active brand and company
        onClearCompany && onClearCompany();
        onClearBrand && onClearBrand(brandInfo._id);
        router.push({
            pathname: siteHomePageUrl,
        });
    };

    private getSiteHomepage = () => {
        const { params } = this.context.router;
        const siteName = params.slug || "";
        return `/site/${siteName}`;
    };

    private renderConfirm() {
        return (
            <Dialog
                autoScrollBodyContent={true}
                contentClassName={`${this.styles}`}
                title={<div className="popupTitle">{format("Your request has been submitted!")}</div>}
                open={this.state.confirmOpen}
                onRequestClose={this.handleCloseConfirm}
                actions={[
                    <Button
                        data-test={DATATEST.REQUEST_BRAND_ACCESS.DISMISS}
                        key="dismiss"
                        label={format("Dismiss")}
                        onClick={this.handleCloseConfirm}
                        className="dialogButton"
                    />,
                    <Button
                        data-test={DATATEST.REQUEST_BRAND_ACCESS.CONNECT_WITH_MORE}
                        primary={true}
                        label={format("Connect with more brands")}
                        onClick={this.redirectToBrands}
                        className="dialogButton"
                        key="connect"
                    />,
                ]}
            >
                <div className="header">{format("Here's what happens next:")}</div>
                <div className="section">
                    <ReviewIcon className="sectionIcon" />
                    <div className="sectionText">
                        {format(
                            "Your request will be reviewed by the brands. This can take anywhere from a couple of hours to a few days. Please be patient"
                        )}
                    </div>
                </div>
                <div className="section">
                    <EmailIcon className="sectionIcon" />
                    <div className="sectionText">
                        {format("You will receive an email once your connection is accepted")}
                    </div>
                </div>
                <div className="section">
                    <CartIcon className="sectionIcon" />
                    <div className="sectionText">{format("Shop the brand and it's latest collections 24/7/365")}</div>
                </div>
                <div className="addition">
                    {format("In certain cases additional information may be needed to complete this request.")}
                </div>
            </Dialog>
        );
    }

    private handleCloseConfirm = () => {
        this.setState({ confirmOpen: false });
        this.props.onCloseDialog();
    };

    private redirectToBrands = () => {
        const { router } = this.context;
        // NOTE: ONLY redirect when it's inside the brand page of the tradeShow Site.
        if (this.props.isBrandPageOfShowSite) {
            this.redirectToSitePage();
        } else {
            router.push("/browse/brands");
            this.props.onCloseDialog();
        }
    };
}
